import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, OnInit, ViewChild, DOCUMENT, inject } from '@angular/core';
import { MatSidenav, MatSidenavContent, MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { UserSession } from '@eemo/api-client';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, Subject } from 'rxjs';
import { AlertComponent } from './components/alert/alert.component';
import { AssumedUserDisconnectComponent } from './components/assumed-user-disconnect/assumed-user-disconnect.component';
import { FloatMenuComponent } from './components/float-menu/float-menu.component';
import { HeaderComponent } from './components/header/header.component';
import { HelpScoutWidgetComponent } from './components/help-scout-widget/help-scout-widget.component';
import { MainMenuComponent } from './components/main-menu/main-menu.component';
import { SubscriptionAlertComponent } from './components/subscription-alert/subscription-alert.component';
import { UserHelper } from './helpers/user.helper';
import { AppConfigService } from './services/app-config.service';
import { AppService } from './services/app.service';
import { AuthenticationService } from './services/authentication.service';
import { BridgeInputService } from './services/bridge/bridge-input.service';
import { BridgeService } from './services/bridge/bridge.service';
import { LoaderService } from './services/loader.service';
import { NavService } from './services/nav.service';
import { VersionCheckService } from './services/version-check.service';

import { AppConstants } from './helpers/app.constants';
import { ActionService } from './services/action.service';
import { version as appVersion } from '../../package.json';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    HeaderComponent,
    MatSidenavModule,
    MainMenuComponent,
    NgTemplateOutlet,
    AlertComponent,
    RouterOutlet,
    SubscriptionAlertComponent,
    AsyncPipe,
    AssumedUserDisconnectComponent,
    HelpScoutWidgetComponent,
    FloatMenuComponent,
  ],
})
export class AppComponent implements OnInit, AfterViewInit {
  authenticationService = inject(AuthenticationService);
  private navService = inject(NavService);
  private deviceService = inject(DeviceDetectorService);
  private loaderService = inject(LoaderService);
  private bridgeService = inject(BridgeService);
  private bridgeInputService = inject(BridgeInputService);
  appConfigService = inject(AppConfigService);
  private titleService = inject(Title);
  private versionCheckService = inject(VersionCheckService);
  private versionUpdateBar = inject(MatSnackBar);
  userHelper = inject(UserHelper);
  appService = inject(AppService);
  private activatedRoute = inject(ActivatedRoute);
  private actionService = inject(ActionService);
  private document = inject(DOCUMENT);

  @ViewChild('snav', { static: true }) navComponent: MatSidenav;
  @ViewChild('snavcontent', { static: true })
  navContentComponent: MatSidenavContent;

  SWIPER_OVERLAY_DIV_ID = AppConstants.SWIPER_OVERLAY_DIV_ID;

  title = 'EEMO';
  opened: boolean;
  navMode = 'side';
  navFixedInViewport = false;

  year: number = Date.now();
  appVersion = appVersion;

  currentUser: UserSession;
  authCookie: string;

  isLoading: Subject<boolean> = this.loaderService.isLoading;
  userConnected: Subject<boolean> = new BehaviorSubject<boolean>(false);
  isMobileApp = false;

  constructor() {
    const appService = this.appService;

    this.detectNativeApp();
    this.appConfigService.detectApplicationType();

    this.titleService.setTitle(this.title);
    this.authenticationService.currentUser.subscribe((x) => (this.currentUser = x));
    this.navService.mainMenuOpened.subscribe((result) => {
      this.opened = result;
    });
    this.navService.openMainMenuNotMobile();

    if (this.deviceService.isMobile() || this.deviceService.isTablet()) {
      this.navMode = 'over';
      this.navFixedInViewport = true;
    }
    this.authenticationService.currentUser.subscribe((u) => {
      this.userConnected.next(this.authenticationService.isUserConnected());
      if (u) {
        appService.saveAvatar(u.userId);
      }
    });
  }

  ngOnInit() {
    // launch the version check service
    this.versionCheckService.initVersionCheck();
    this.versionCheckService.versionUpdated.subscribe((updated) => {
      if (updated) {
        const barRef = this.versionUpdateBar.open(
          "Une nouvelle version de l'application est disponible",
          'Mettre à jour',
          {
            duration: 0,
          },
        );
        barRef.onAction().subscribe(() => {
          this.updateVersion();
        });
      }
    });

    // Render the HTTPCS badge
    // FIXME remove httpcs badge until the subscription is renewed
    /*if (environment.httpcsEnabled) {
      const s = this.renderer2.createElement("script");
      s.type = "text/javascript";
      s.src = environment.httpcsUrl;
      s.text = "";
      s.defer = true;
      s.async = true;
      this.renderer2.appendChild(this.document.body, s);
    }*/
  }

  ngAfterViewInit(): void {
    // show help scout message if provided in the url
    const messageId = this.activatedRoute.queryParams[AppConstants.PARAM_BEACON_MESSAGE_ID];
    if (messageId) {
      this.actionService.showHelpScoutMessage(messageId);
    }
  }

  detectNativeApp() {
    this.isMobileApp = this.appConfigService.isMobileApp;
    if (this.isMobileApp) {
      this.initNativeAppBridgeService();
    } else {
      this.activatedRoute.queryParams.subscribe((values) => {
        if (AppConfigService.isParamMobileApp(values)) {
          this.initNativeAppBridgeService();
        }
      });
    }
  }

  initNativeAppBridgeService() {
    // tslint:disable-next-line
    window['BridgeService'] = this.bridgeService;
    // tslint:disable-next-line
    window['BridgeInputService'] = this.bridgeInputService;
    this.appConfigService.isMobileApp = true;
    this.isMobileApp = true;
    this.appService.initForNative();
    // on native app, the detection type must be rerun after is mobile app is set to true
    this.appConfigService.detectApplicationType();
  }

  isNavigationDisabled(): boolean {
    return this.userHelper.isRescueService();
  }

  updateVersion() {
    location.reload();
  }

  deviceIsDesktop() {
    return this.deviceService.isDesktop();
  }

  onActivate(_event: unknown) {}

  onBackDropClick() {
    this.navService.toggleMainMenu();
  }
}
